/**
 * Counting, honestly.
 *
 * ===========================================================================
 * THE API HAS NO TOTALS, AND THIS FILE REFUSES TO PRETEND OTHERWISE
 * ===========================================================================
 * Every staff list is CURSOR-paginated (`kernel/api/pagination.py`). A cursor page
 * carries `next`, `previous` and `results` — and deliberately no `count`, because a
 * cursor genuinely has no position and no total. There is no analytics endpoint, no
 * aggregate route and no `?count=true`.
 *
 * So a "count" on the reports screen is exactly one thing: the number of records this
 * reader could see on one page of at most 100. When there is another page, the figure
 * is a FLOOR and this hook says so through `truncated`, which the screen renders as
 * "100+" rather than as "100". A report that quietly shows 100 when the answer is 4,000
 * is worse than no report: somebody will make a decision on it.
 *
 * Scope is the server's, applied inside the query rather than by hiding widgets — so
 * every figure here is already "within what this reader is permitted to see", which the
 * screen states once rather than caveating per tile.
 * ===========================================================================
 *
 * Do not "fix" this by summing pages in a loop. Walking a cursor to build a total is a
 * denial-of-service against your own database on any real tenant, and it would still be
 * a snapshot taken over several seconds rather than a count.
 */

import { useApiQuery, type CursorPage, type ListParams } from '@legalcrm/api';
import type { QueryKey } from '@tanstack/react-query';

/** The maximum the backend allows (`StandardCursorPagination.max_page_size`). */
export const TALLY_LIMIT = 100;

export interface Tally<T> {
  rows: T[];
  /** Records seen. A floor, not a total, when `truncated`. */
  count: number;
  /** There is at least one more page: the count understates the true figure. */
  truncated: boolean;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
}

/**
 * One page of a list, ready to be grouped.
 *
 * `enabled` is how a card is kept from firing at all when the reader holds no
 * capability for that module — see the note on §3.5 in the frontend README. A 403 in
 * the network tab for a widget the user was never meant to see is noise at best and a
 * disclosure at worst.
 */
export function useTally<T>(
  key: QueryKey,
  path: string,
  params: ListParams = {},
  enabled = true,
): Tally<T> {
  const query = useApiQuery<CursorPage<T>>(key, path, {
    query: { ...params, limit: TALLY_LIMIT },
    enabled,
  });

  const rows = query.data?.results ?? [];
  return {
    rows,
    count: rows.length,
    truncated: Boolean(query.data?.next),
    isLoading: query.isLoading && enabled,
    error: query.error,
    refetch: () => void query.refetch(),
  };
}

/** Group rows into ordered buckets, keeping empty ones so a stage with none still shows. */
export function tallyBy<T>(
  rows: T[],
  keyOf: (row: T) => string,
  order: Array<{ key: string; label: string }>,
): Array<{ key: string; label: string; value: number }> {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const key = keyOf(row);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const known = order.map((bucket) => ({
    ...bucket,
    value: counts.get(bucket.key) ?? 0,
  }));

  // Anything the firm has configured that the caller did not list still gets a row.
  // Silently dropping it would make the columns not add up to the total, which is the
  // fastest way to lose a reader's trust in a figure.
  const listed = new Set(order.map((bucket) => bucket.key));
  const extra = [...counts.entries()]
    .filter(([key]) => !listed.has(key))
    .map(([key, value]) => ({ key, label: key, value }));

  return [...known, ...extra];
}
